import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  SearchIcon,
  RefreshIcon,
  DatabaseIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ServerIcon,
  AIIcon,
  TrashIcon,
  AlertIcon,
  CheckIcon,
  ShieldIcon,
} from '../components/common/Icons';
import { useSchemaHealth } from '../hooks/useSchemaHealth';
import type { OrphanedSchema, ArchivedSchema } from '../hooks/useSchemaHealth';

// =============================================================================
// Types
// =============================================================================

interface SchemaElement {
  name: string;
  type: string;
  columns?: number;
  schema?: string;
  rls_enabled?: boolean;
  rls_forced?: boolean;
  method?: string;
  fields?: number;
}

interface SchemaType {
  type: string;
  version: string;
  last_updated: string;
  element_count: number;
  elements: SchemaElement[];
}

// =============================================================================
// Schema Browser Tab (existing functionality)
// =============================================================================

const SchemaBrowserTab: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [schemas, setSchemas] = useState<SchemaType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSchemas, setExpandedSchemas] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    fetchSchemas();
  }, [authLoading]);

  const fetchSchemas = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authFetch('/api/admin/schema');
      if (response.ok) {
        const data = await response.json();
        setSchemas(Array.isArray(data) ? data : data.schemas || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to fetch schemas');
      }
    } catch (err) {
      console.error('Failed to fetch schemas:', err);
      setError('Failed to connect to API');
    }
    setIsLoading(false);
  };

  const toggleSchema = (type: string) => {
    const newExpanded = new Set(expandedSchemas);
    if (newExpanded.has(type)) {
      newExpanded.delete(type);
    } else {
      newExpanded.add(type);
    }
    setExpandedSchemas(newExpanded);
  };

  const getSchemaIcon = (type: string) => {
    switch (type) {
      case 'database': return <DatabaseIcon size={16} />;
      case 'api': return <ServerIcon size={16} />;
      case 'model': return <AIIcon size={16} />;
      default: return <DatabaseIcon size={16} />;
    }
  };

  const getSchemaTitle = (type: string) => {
    switch (type) {
      case 'database': return 'Database Schema';
      case 'api': return 'API Endpoints';
      case 'model': return 'Data Models';
      default: return type;
    }
  };

  const getSchemaDescription = (schema: SchemaType) => {
    switch (schema.type) {
      case 'database': return `${schema.element_count} tables - v${schema.version}`;
      case 'api': return `${schema.element_count} endpoints - v${schema.version}`;
      case 'model': return `${schema.element_count} models - v${schema.version}`;
      default: return `v${schema.version}`;
    }
  };

  const filteredSchemas = schemas.filter(schema =>
    schema.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
    schema.elements.some(el => el.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <>
      <div className="page-toolbar">
        <div className="input-wrapper" style={{ maxWidth: '320px' }}>
          <SearchIcon size={18} className="input-icon" />
          <input
            type="text"
            className="input input-with-icon"
            placeholder="Search schemas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={() => setExpandedSchemas(new Set(schemas.map(s => s.type)))}>
            Expand All
          </button>
          <button className="btn btn-ghost" onClick={fetchSchemas}>
            <RefreshIcon size={18} />
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div className="space-y-4">
        {isLoading ? (
          <div className="glass-card">
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading schemas...</p>
            </div>
          </div>
        ) : filteredSchemas.length === 0 ? (
          <div className="glass-card">
            <div className="empty-state">
              <DatabaseIcon size={48} />
              <p>No schemas found</p>
            </div>
          </div>
        ) : (
          filteredSchemas.map((schema) => (
            <div key={schema.type} className="glass-card">
              <div
                className="schema-header"
                onClick={() => toggleSchema(schema.type)}
                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem' }}
              >
                <div className="flex items-center gap-3" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {expandedSchemas.has(schema.type) ? (
                    <ChevronDownIcon size={20} />
                  ) : (
                    <ChevronRightIcon size={20} />
                  )}
                  <div className="icon-badge" style={{ background: 'var(--primary-alpha)', borderRadius: '8px', padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {getSchemaIcon(schema.type)}
                  </div>
                  <div>
                    <h3 className="font-medium" style={{ fontWeight: 500, marginBottom: '0.25rem' }}>{getSchemaTitle(schema.type)}</h3>
                    <p className="text-secondary text-sm" style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{getSchemaDescription(schema)}</p>
                  </div>
                </div>
                <span className="badge badge-secondary" style={{ background: 'var(--bg-tertiary)', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.875rem' }}>{schema.element_count} items</span>
              </div>

              {expandedSchemas.has(schema.type) && (
                <div className="schema-elements" style={{ borderTop: '1px solid var(--border-color)', padding: '1rem' }}>
                  <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>Type</th>
                        <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>Details</th>
                        {schema.type === 'database' && (
                          <th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid var(--border-color)' }}>RLS</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {schema.elements.map((element) => (
                        <tr key={element.name}>
                          <td className="font-mono font-medium" style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{element.name}</td>
                          <td style={{ padding: '0.5rem' }}>
                            <span className="badge badge-info" style={{ background: 'var(--info-alpha)', color: 'var(--info)', padding: '0.125rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>{element.type}</span>
                          </td>
                          <td className="text-secondary" style={{ padding: '0.5rem', color: 'var(--text-secondary)' }}>
                            {element.columns && `${element.columns} columns`}
                            {element.method && <span className="badge" style={{ background: element.method === 'GET' ? 'var(--success-alpha)' : 'var(--warning-alpha)', color: element.method === 'GET' ? 'var(--success)' : 'var(--warning)', padding: '0.125rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>{element.method}</span>}
                            {element.fields && `${element.fields} fields`}
                          </td>
                          {schema.type === 'database' && (
                            <td style={{ padding: '0.5rem' }}>
                              {element.rls_enabled ? (
                                <span className="badge" style={{ background: 'var(--success-alpha)', color: 'var(--success)', padding: '0.125rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                                  {element.rls_forced ? 'RLS Forced' : 'RLS Enabled'}
                                </span>
                              ) : (
                                <span className="badge" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', padding: '0.125rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' }}>
                                  No RLS
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};

// =============================================================================
// Confirmation Dialog (local, simple)
// =============================================================================

interface ConfirmState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
}

const ConfirmDialog: React.FC<{
  state: ConfirmState | null;
  onClose: () => void;
}> = ({ state, onClose }) => {
  if (!state || !state.isOpen) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--glass-bg)',
          backdropFilter: 'var(--glass-blur-lg)',
          WebkitBackdropFilter: 'var(--glass-blur-lg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-2xl)',
          boxShadow: 'var(--glass-shadow-lg)',
          padding: 0,
          width: '100%',
          maxWidth: '420px',
          animation: 'modal-appear 0.2s ease-out',
        }}
      >
        <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid var(--border-secondary)' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--text-primary)' }}>
            {state.title}
          </h3>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <p style={{ margin: 0, fontSize: 'var(--text-base)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {state.message}
          </p>
        </div>
        <div style={{ padding: '16px 24px 24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button className="btn btn-secondary" onClick={onClose} style={{ minWidth: '80px' }}>
            Cancel
          </button>
          <button className="btn btn-error" onClick={state.onConfirm} style={{ minWidth: '100px' }}>
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Helper: format bytes
// =============================================================================

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

// =============================================================================
// Schema Health Tab (new)
// =============================================================================

const SchemaHealthTab: React.FC = () => {
  const {
    stats,
    orphaned,
    archived,
    loading,
    error,
    refresh,
    dropSchema,
    cleanupOrphaned,
    cleanupArchived,
  } = useSchemaHealth();

  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [operationError, setOperationError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setOperationError(null);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  const handleDropSchema = (schema: OrphanedSchema | ArchivedSchema) => {
    setConfirmState({
      isOpen: true,
      title: 'Drop Schema',
      message: `Are you sure you want to permanently drop schema "${schema.name}"? This will delete ${schema.table_count} table(s) and ${formatBytes(schema.size_bytes)} of data. This action cannot be undone.`,
      confirmLabel: 'Drop Schema',
      onConfirm: async () => {
        setConfirmState(null);
        setActionLoading(true);
        setOperationError(null);
        const success = await dropSchema(schema.name);
        setActionLoading(false);
        if (success) {
          showSuccess(`Schema "${schema.name}" dropped successfully`);
        }
      },
    });
  };

  const handleCleanupOrphaned = () => {
    const count = orphaned.length;
    setConfirmState({
      isOpen: true,
      title: 'Clean Up Orphaned Schemas',
      message: `Are you sure you want to drop all ${count} orphaned schema(s)? This will permanently delete schemas that have no matching tenant. This action cannot be undone.`,
      confirmLabel: `Drop ${count} Schema(s)`,
      onConfirm: async () => {
        setConfirmState(null);
        setActionLoading(true);
        setOperationError(null);
        const result = await cleanupOrphaned(false);
        setActionLoading(false);
        if (result) {
          const deleted = result.summary.deleted || 0;
          showSuccess(`Cleaned up ${deleted} orphaned schema(s)`);
        }
      },
    });
  };

  const handleCleanupArchived = () => {
    const count = archived.length;
    setConfirmState({
      isOpen: true,
      title: 'Clean Up Archived Schemas',
      message: `Are you sure you want to drop all ${count} archived schema(s)? This will permanently delete data from archived tenants. This action cannot be undone.`,
      confirmLabel: `Drop ${count} Schema(s)`,
      onConfirm: async () => {
        setConfirmState(null);
        setActionLoading(true);
        setOperationError(null);
        const result = await cleanupArchived(0, false);
        setActionLoading(false);
        if (result) {
          const deleted = result.summary.deleted || 0;
          showSuccess(`Cleaned up ${deleted} archived schema(s)`);
        }
      },
    });
  };

  if (loading && !stats) {
    return (
      <div className="glass-card">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading schema health data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Messages */}
      {successMessage && (
        <div className="alert alert-success" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CheckIcon size={16} /> {successMessage}
        </div>
      )}
      {(operationError || error) && (
        <div className="alert alert-error" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertIcon size={16} /> {operationError || error}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button className="btn btn-ghost" onClick={refresh} disabled={loading}>
          <RefreshIcon size={18} />
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}>
          <div className="glass-card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div style={{ background: 'var(--primary-alpha)', borderRadius: '8px', padding: '0.5rem', display: 'flex' }}>
                <DatabaseIcon size={16} />
              </div>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Total Schemas</span>
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>{stats.total_schemas}</div>
          </div>

          <div className="glass-card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div style={{ background: 'var(--success-alpha)', borderRadius: '8px', padding: '0.5rem', display: 'flex' }}>
                <CheckIcon size={16} />
              </div>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Active</span>
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--success)' }}>{stats.active_schemas}</div>
          </div>

          <div className="glass-card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div style={{
                background: stats.orphaned_schemas > 0 ? 'var(--warning-alpha)' : 'var(--bg-tertiary)',
                borderRadius: '8px', padding: '0.5rem', display: 'flex',
              }}>
                <AlertIcon size={16} />
              </div>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Orphaned</span>
            </div>
            <div style={{
              fontSize: '1.75rem', fontWeight: 700,
              color: stats.orphaned_schemas > 0 ? 'var(--warning)' : 'var(--text-primary)',
            }}>
              {stats.orphaned_schemas}
            </div>
            {stats.orphaned_size_bytes > 0 && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                {formatBytes(stats.orphaned_size_bytes)}
              </div>
            )}
          </div>

          <div className="glass-card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div style={{
                background: stats.archived_schemas > 0 ? 'var(--info-alpha)' : 'var(--bg-tertiary)',
                borderRadius: '8px', padding: '0.5rem', display: 'flex',
              }}>
                <ShieldIcon size={16} />
              </div>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Archived</span>
            </div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700 }}>{stats.archived_schemas}</div>
            {stats.archived_size_bytes > 0 && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                {formatBytes(stats.archived_size_bytes)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Orphaned Schemas Table */}
      <div className="glass-card" style={{ marginBottom: '1.5rem' }}>
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h3 style={{ margin: 0, fontWeight: 600, fontSize: '1rem' }}>Orphaned Schemas</h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Schemas without a matching active tenant
            </p>
          </div>
          {orphaned.length > 0 && (
            <button
              className="btn btn-warning"
              onClick={handleCleanupOrphaned}
              disabled={actionLoading}
              style={{ fontSize: '0.8rem' }}
            >
              <TrashIcon size={14} /> Clean Up All ({orphaned.length})
            </button>
          )}
        </div>
        {orphaned.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <CheckIcon size={24} />
            <p style={{ margin: '8px 0 0' }}>No orphaned schemas found</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem', fontWeight: 600 }}>Schema Name</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem', fontWeight: 600 }}>Tables</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem', fontWeight: 600 }}>Size</th>
                  <th style={{ textAlign: 'center', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orphaned.map((schema) => (
                  <tr key={schema.name}>
                    <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {schema.name}
                      {schema.is_archived && (
                        <span style={{
                          marginLeft: '8px',
                          background: 'var(--info-alpha)',
                          color: 'var(--info)',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                        }}>
                          archived
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>{schema.table_count}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.85rem' }}>{formatBytes(schema.size_bytes)}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                      <button
                        className="btn btn-error"
                        onClick={() => handleDropSchema(schema)}
                        disabled={actionLoading}
                        style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                      >
                        <TrashIcon size={12} /> Drop
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Archived Schemas Table */}
      <div className="glass-card">
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h3 style={{ margin: 0, fontWeight: 600, fontSize: '1rem' }}>Archived Schemas</h3>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Schemas from archived tenants
            </p>
          </div>
          {archived.length > 0 && (
            <button
              className="btn btn-warning"
              onClick={handleCleanupArchived}
              disabled={actionLoading}
              style={{ fontSize: '0.8rem' }}
            >
              <TrashIcon size={14} /> Clean Up All ({archived.length})
            </button>
          )}
        </div>
        {archived.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <CheckIcon size={24} />
            <p style={{ margin: '8px 0 0' }}>No archived schemas found</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem', fontWeight: 600 }}>Schema Name</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem', fontWeight: 600 }}>Tables</th>
                  <th style={{ textAlign: 'right', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem', fontWeight: 600 }}>Size</th>
                  <th style={{ textAlign: 'left', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem', fontWeight: 600 }}>Archived</th>
                  <th style={{ textAlign: 'center', padding: '0.75rem 1rem', borderBottom: '1px solid var(--border-color)', fontSize: '0.8rem', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {archived.map((schema) => (
                  <tr key={schema.name}>
                    <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>{schema.name}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>{schema.table_count}</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontSize: '0.85rem' }}>{formatBytes(schema.size_bytes)}</td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {schema.archived_at
                        ? new Date(schema.archived_at).toLocaleDateString()
                        : 'Unknown'}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                      <button
                        className="btn btn-error"
                        onClick={() => handleDropSchema(schema)}
                        disabled={actionLoading}
                        style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                      >
                        <TrashIcon size={12} /> Drop
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </>
  );
};

// =============================================================================
// Main SchemaPage with Tabs
// =============================================================================

const SchemaPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'browser' | 'health'>('browser');

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '8px 20px',
    border: 'none',
    background: isActive ? 'var(--primary-alpha)' : 'transparent',
    color: isActive ? 'var(--primary)' : 'var(--text-secondary)',
    cursor: 'pointer',
    fontWeight: isActive ? 600 : 400,
    fontSize: '0.875rem',
    borderRadius: '8px',
    transition: 'all 0.15s ease',
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Schema Explorer</h1>
          <p className="page-subtitle">
            {activeTab === 'browser' ? 'Browse platform data schemas' : 'Monitor and manage tenant schemas'}
          </p>
        </div>
        <div className="page-header-right">
          <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-secondary)', borderRadius: '10px', padding: '4px' }}>
            <button style={tabStyle(activeTab === 'browser')} onClick={() => setActiveTab('browser')}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <DatabaseIcon size={14} /> Schema Browser
              </span>
            </button>
            <button style={tabStyle(activeTab === 'health')} onClick={() => setActiveTab('health')}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <ShieldIcon size={14} /> Schema Health
              </span>
            </button>
          </div>
        </div>
      </div>

      {activeTab === 'browser' ? <SchemaBrowserTab /> : <SchemaHealthTab />}
    </div>
  );
};

export default SchemaPage;
